/**
 * Item Location Balance Module - MIGRATED TO REUSABLE WAREHOUSE TABLE
 *
 * This demonstrates the new ItemLocationBalance implementation using the reusable
 * WarehouseTable system, showing 82% code reduction (830+ lines → 150 lines)
 * while maintaining identical functionality and appearance.
 *
 * Features maintained:
 * - Compact table with professional blue warehouse theme
 * - Excel-like column configuration and filtering
 * - Wildcard search patterns with % support
 * - Column sorting, resizing, and reordering
 * - Summary calculations in footer
 * - CSV export functionality
 * - All original styling and behavior preserved
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  Package,
  Search,
  Filter,
  RefreshCw,
  Loader2,
  AlertCircle,
  Warehouse,
  ChevronDown,
  ChevronUp,
  Info,
  Pencil,
  Tag,
} from 'lucide-react';
import { useAuth } from '../../../../contexts/AuthContext';
import { useTranslation } from '../../../../contexts/I18nProvider';
import { itemLocationBalanceService } from '../../../../services/itemLocationBalanceService';
import {
  ItemLocationBalanceDto,
  ItemBalanceFilters,
} from '../../../../types/itemLocationBalance';

// Import the new reusable warehouse table system
import { WarehouseTable } from '../../../shared/table';
import { createColumnFactory } from '../../../shared/table/utils/tableUtils';
import type { ColumnDefinition, SummaryCalculation, RowAction } from '../../../shared/table/types/table';
import { BackButton } from '../../../common/BackButton';
import { CorrectSerialModal } from './CorrectSerialModal';
import { CorrectBatchModal } from './CorrectBatchModal';

// Create column factory for type-safe column definitions
const columnFactory = createColumnFactory<ItemLocationBalanceDto>();

interface ItemLocationBalanceAppProps {
  onBack?: () => void;
}

export const ItemLocationBalanceApp: React.FC<ItemLocationBalanceAppProps> = ({ onBack }) => {
  const { user } = useAuth();
  const { t } = useTranslation();

  // Data state
  const [data, setData] = useState<ItemLocationBalanceDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Simple top-level filters
  const [globalSearch, setGlobalSearch] = useState('');
  const [showEmptyLocations, setShowEmptyLocations] = useState(false);
  const [showInfo, setShowInfo] = useState(false);

  // Rett serienummer / batch+utløpsdato (kun lagersjef/administrator)
  const [correctSerialRow, setCorrectSerialRow] = useState<ItemLocationBalanceDto | null>(null);
  const [correctBatchRow, setCorrectBatchRow] = useState<ItemLocationBalanceDto | null>(null);
  const isManagerOrAdmin = user?.operatorGroupId === '2' || user?.operatorGroupId === '3';

  // Batch-varetyper (StatusType 32): 1 = Batchnummer, 4 = m/holdbarhet, 5 = m/lagerstyring.
  // Opptrer aldri sammen med serienummer (varetype 6) — de to rette-handlingene er disjunkte.
  const isBatchItemType = (row: ItemLocationBalanceDto) =>
    row.itemType === 1 || row.itemType === 4 || row.itemType === 5;

  /**
   * Define columns using the type-safe column factory
   */
  const columns: ColumnDefinition<ItemLocationBalanceDto>[] = useMemo(() => [
    columnFactory.createTextColumn('warehouseID', 'warehouseID', t('manager.modules.itemLocationBalance.columns.warehouseID', {}, 'Lagersted'), {
      width: 80,
      sortable: true,
      filterType: 'both'
    }),
    columnFactory.createTextColumn('binCode', 'binCode', t('warehouse.general.bin', {}, 'Lokasjon'), {
      width: 100,
      sortable: true,
      filterType: 'both'
    }),
    columnFactory.createTextColumn('itemNo', 'itemNo', t('warehouse.general.itemNumber', {}, 'Varenummer'), {
      width: 120,
      sortable: true,
      filterType: 'both'
    }),
    columnFactory.createTextColumn('itemDescription', 'itemDescription', t('manager.modules.itemLocationBalance.columns.itemDescription', {}, 'Varebeskrivelse'), {
      width: 200,
      sortable: true,
      filterType: 'both'
    }),
    columnFactory.createTextColumn('itemBarCode', 'itemBarCode', t('manager.modules.itemLocationBalance.columns.itemBarCode', {}, 'Strekkode'), {
      width: 120,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('itemGrp1', 'itemGrp1', t('manager.modules.itemLocationBalance.columns.itemGrp1', {}, 'Varegruppe 1'), {
      width: 100,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('itemGrp2', 'itemGrp2', t('manager.modules.itemLocationBalance.columns.itemGrp2', {}, 'Varegruppe 2'), {
      width: 100,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('itemGrp3', 'itemGrp3', t('manager.modules.itemLocationBalance.columns.itemGrp3', {}, 'Varegruppe 3'), {
      width: 100,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createNumberColumn('quantity', 'quantity', t('common.fields.quantity', {}, 'Antall'), {
      width: 80,
      sortable: true,
      filterType: 'both',
      formatter: (value: any) => Number(value).toLocaleString('no-NO')
    }),
    columnFactory.createNumberColumn('maxQuantity', 'maxQuantity', t('manager.modules.itemLocationBalance.columns.maxQuantity', {}, 'Maks kap.'), {
      width: 70,
      sortable: true,
      filterType: 'both',
      formatter: (value: any) => value != null ? Number(value).toLocaleString('no-NO') : '-'
    }),
    columnFactory.createNumberColumn('availableSpace', 'availableSpace', t('manager.modules.itemLocationBalance.columns.availableSpace', {}, 'Ledig kap.'), {
      width: 70,
      sortable: true,
      filterType: 'both',
      formatter: (value: any) => value != null ? Number(value).toLocaleString('no-NO') : '-'
    }),
    columnFactory.createNumberColumn('quantityReserved', 'quantityReserved', t('warehouse.general.reserved', {}, 'Reservert'), {
      width: 80,
      sortable: true,
      filterType: 'both',
      formatter: (value: any) => Number(value || 0).toLocaleString('no-NO')
    }),
    columnFactory.createNumberColumn('quantityAvailable', 'quantityAvailable', t('warehouse.general.available', {}, 'Tilgjengelig'), {
      width: 90,
      sortable: true,
      filterType: 'both',
      formatter: (value: any, row: ItemLocationBalanceDto) =>
        Number(value ?? row.quantity - (row.quantityReserved || 0)).toLocaleString('no-NO')
    }),
    columnFactory.createTextColumn('serialNo', 'serialNo', t('manager.modules.itemLocationBalance.columns.serialNo', {}, 'Serienr.'), {
      width: 110,
      sortable: true,
      filterType: 'both'
    }),
    columnFactory.createDateColumn('lastMovementDate', 'lastMovementDate', t('manager.modules.itemLocationBalance.columns.lastMovementDate', {}, 'Siste Trans.'), {
      width: 90,
      sortable: true,
      filterType: 'both'
    }),
    columnFactory.createDateColumn('lastCountedDate', 'lastCountedDate', t('manager.modules.itemLocationBalance.columns.lastCountedDate', {}, 'Siste Telling'), {
      width: 90,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createDateColumn('lastReplenishDate', 'lastReplenishDate', t('manager.modules.itemLocationBalance.columns.lastReplenishDate', {}, 'Siste Påfyll'), {
      width: 90,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('grShipmentNo', 'grShipmentNo', t('manager.modules.itemLocationBalance.columns.grShipmentNo', {}, 'Partinr.'), {
      width: 80,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('batchNo', 'batchNo', t('manager.modules.itemLocationBalance.columns.batchNo', {}, 'Batchnr.'), {
      width: 80,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createDateColumn('expiryDate', 'expiryDate', t('manager.modules.itemLocationBalance.columns.expiryDate', {}, 'Utløpsdato'), {
      width: 90,
      sortable: true,
      filterType: 'both'
    })
  ], [t]);

  /**
   * Define summary calculations for footer
   */
  const summaryCalculations: SummaryCalculation<ItemLocationBalanceDto>[] = useMemo(() => [
    { column: 'quantity', type: 'sum' },
    { column: 'quantityReserved', type: 'sum' },
    { 
      column: 'quantityAvailable', 
      type: 'custom',
      customCalculation: (data) => data.reduce((sum, item) =>
        sum + (item.quantityAvailable ?? item.quantity - (item.quantityReserved || 0)), 0
      )
    }
  ], []);

  /**
   * Rad-handlinger (kun lagersjef/administrator):
   * - Rett serienummer: kun aktiv på rader med serienummer (varetype 6)
   * - Rett batch/utløpsdato: kun aktiv på batch-varetyper (1/4/5) uten serienummer
   */
  const correctionRowActions: RowAction<ItemLocationBalanceDto>[] = useMemo(() => [
    {
      id: 'correct-serial',
      label: t('manager.modules.itemLocationBalance.serialCorrection.action', {}, 'Rett serienummer'),
      icon: Pencil,
      disabled: (row: ItemLocationBalanceDto) => !row.serialNo,
      onClick: (row: ItemLocationBalanceDto) => {
        if (row.serialNo) setCorrectSerialRow(row);
      }
    },
    {
      id: 'correct-batch',
      label: t('manager.modules.itemLocationBalance.batchCorrection.action', {}, 'Rett batch/utløpsdato'),
      icon: Tag,
      disabled: (row: ItemLocationBalanceDto) => !!row.serialNo || !isBatchItemType(row),
      onClick: (row: ItemLocationBalanceDto) => {
        if (!row.serialNo && isBatchItemType(row)) setCorrectBatchRow(row);
      }
    }
  ], [t]);

  /**
   * Apply top-level filters before passing to table
   */
  const filteredData = useMemo(() => {
    let result = [...data];

    // Global search with wildcard support
    if (globalSearch) {
      const searchLower = globalSearch.toLowerCase();
      result = result.filter((item) => {
        const itemNo = item.itemNo.toLowerCase();
        const itemDesc = (item.itemDescription || '').toLowerCase();
        const binCode = item.binCode.toLowerCase();
        
        if (searchLower.includes('%')) {
          // Wildcard pattern matching
          const regexPattern = searchLower
            .split('')
            .map(char => char === '%' ? '.*' : char.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
            .join('');
          
          try {
            const regex = new RegExp(`^${regexPattern}$`, 'i');
            return regex.test(itemNo) || regex.test(itemDesc) || regex.test(binCode);
          } catch (e) {
            return itemNo.includes(searchLower.replace(/%/g, '')) ||
                   itemDesc.includes(searchLower.replace(/%/g, '')) ||
                   binCode.includes(searchLower.replace(/%/g, ''));
          }
        } else {
          return itemNo.includes(searchLower) ||
                 itemDesc.includes(searchLower) ||
                 binCode.includes(searchLower);
        }
      });
    }

    // «Uten saldo» av: kun positiv saldo. På: alt (også 0 og negativ — hentes med includeZero)
    if (!showEmptyLocations) {
      result = result.filter((item) => item.quantity > 0);
    }

    return result;
  }, [data, globalSearch, showEmptyLocations]);


  /**
   * Load data on mount, og på nytt når «Uten saldo» endres (saldo 0 hentes kun ved behov)
   */
  useEffect(() => {
    loadItemBins();
  }, [showEmptyLocations]);

  /**
   * Fetch data from API
   */
  const loadItemBins = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await itemLocationBalanceService.getAllItemBins(showEmptyLocations);
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load item location balances');
      console.error('Error loading item bins:', err);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Loading state — KUN ved førstegangslasting (ingen data ennå). Ved «Oppdater» må
   * WarehouseTable forbli montert, ellers nullstilles brukerens kolonnevalg
   * (synlighet/bredde/rekkefølge er komponent-state); tabellen viser egen lasteindikator
   * via loading-prop'en.
   */
  if (loading && data.length === 0) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-teal-600 mx-auto mb-4" />
          <p className="text-gray-600">{t('manager.modules.itemLocationBalance.loading', {}, 'Laster varelokasjonssaldo...')}</p>
        </div>
      </div>
    );
  }

  /**
   * Error state — fullskjerm kun når vi ikke har data å vise (samme monterings-hensyn
   * som loading; ved feil under oppdatering viser WarehouseTable feilen via error-prop'en)
   */
  if (error && data.length === 0) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center max-w-md">
          <AlertCircle className="w-12 h-12 text-red-600 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">{t('manager.modules.itemLocationBalance.errors.loadFailed', {}, 'Kunne ikke laste data')}</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={loadItemBins}
            className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors inline-flex items-center gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            {t('manager.modules.itemLocationBalance.buttons.retry', {}, 'Prøv igjen')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full overflow-hidden bg-gray-50">
      {/* Header with search and filters */}
      <div className="sticky top-0 z-20 bg-white border-b border-gray-300">
        <div className="px-4 py-2 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <BackButton onClick={onBack || (() => {})} />
            <Package className="w-5 h-5 text-teal-600" />
            <h1 className="text-lg font-semibold text-gray-900">{t('manager.modules.itemLocationBalance.title', {}, 'Varelokasjonssaldo')}</h1>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
              <input
                type="text"
                value={globalSearch}
                onChange={(e) => setGlobalSearch(e.target.value)}
                placeholder={t('manager.modules.itemLocationBalance.searchPlaceholder', {}, 'Søk varenr/beskrivelse/lokasjon...')}
                className="w-80 pl-8 pr-3 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-teal-500 focus:border-transparent"
              />
            </div>
            <label className="flex items-center gap-1 cursor-pointer text-sm">
              <input
                type="checkbox"
                checked={showEmptyLocations}
                onChange={(e) => setShowEmptyLocations(e.target.checked)}
                className="w-3 h-3 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
              />
              <span>{t('manager.modules.itemLocationBalance.showEmptyLocations', {}, 'Uten saldo')}</span>
            </label>
            <button
              onClick={loadItemBins}
              className="px-3 py-1.5 text-sm text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 inline-flex items-center gap-1"
              disabled={loading}
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
              {t('common.buttons.refresh', {}, 'Oppdater')}
            </button>
          </div>
        </div>

        {/* Info toggle */}
        <div className="px-4 pb-2">
          <button
            type="button"
            onClick={() => setShowInfo(prev => !prev)}
            className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 focus:outline-none"
            aria-expanded={showInfo}
          >
            <Info size={14} />
            <span>{t('manager.modules.itemLocationBalance.info.toggle', {}, 'Hva er Varelokasjonssaldo?')}</span>
            {showInfo ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>

          {showInfo && (
            <div className="mt-2 mb-1 bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-gray-800">
              <div className="mb-3">
                <h3 className="font-bold text-gray-900 mb-1">{t('manager.modules.itemLocationBalance.info.purpose.heading', {}, 'Hensikt')}</h3>
                <p>
                  {t('manager.modules.itemLocationBalance.info.purpose.body', {}, 'Varelokasjonssaldo viser hvor mye av hver vare som ligger på hver enkelt lokasjon — med saldo, reservasjoner, tilgjengelig antall og hvor mye plass det er igjen i lokasjonen. Brukes for å spore saldo per bin, finne ledig kapasitet før påfyll, og se hva som er reservert mot åpne plukkordrer.')}
                </p>
              </div>

              <div className="mb-3">
                <h3 className="font-bold text-gray-900 mb-1">{t('manager.modules.itemLocationBalance.info.columnsHeading', {}, 'Kolonnene og hvordan de beregnes')}</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs border border-blue-200 rounded">
                    <thead>
                      <tr className="bg-blue-100">
                        <th className="text-left py-1.5 px-2 font-semibold border-b border-blue-200">{t('manager.modules.itemLocationBalance.info.table.columnHeader', {}, 'Kolonne')}</th>
                        <th className="text-left py-1.5 px-2 font-semibold border-b border-blue-200">{t('manager.modules.itemLocationBalance.info.table.calculationHeader', {}, 'Beregning')}</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white">
                      <tr className="border-b border-blue-100">
                        <td className="py-1.5 px-2 font-medium">{t('common.fields.quantity', {}, 'Antall')}</td>
                        <td className="py-1.5 px-2">
                          <code className="bg-blue-50 px-1 rounded">ItemBins.Quantity</code> {t('manager.modules.itemLocationBalance.info.calc.quantity', {}, '— faktisk saldo på lokasjonen')}
                        </td>
                      </tr>
                      <tr className="border-b border-blue-100">
                        <td className="py-1.5 px-2 font-medium">{t('manager.modules.itemLocationBalance.columns.maxQuantity', {}, 'Maks kap.')}</td>
                        <td className="py-1.5 px-2">
                          <code className="bg-blue-50 px-1 rounded">ItemBinSize.MaxQuantity</code> {t('manager.modules.itemLocationBalance.info.calc.maxQuantityPart1', {}, '— først direkte per-vare (')}<code className="bg-blue-50 px-1 rounded">ItemBinSize.ItemNo = vare</code>{t('manager.modules.itemLocationBalance.info.calc.maxQuantityPart2', {}, '), så gruppe-basert fallback (')}<code className="bg-blue-50 px-1 rounded">ItemBinSizeGroupID</code>{t('manager.modules.itemLocationBalance.info.calc.maxQuantityPart3', {}, ' på varen)')}
                        </td>
                      </tr>
                      <tr className="border-b border-blue-100">
                        <td className="py-1.5 px-2 font-medium">{t('manager.modules.itemLocationBalance.columns.availableSpace', {}, 'Ledig kap.')}</td>
                        <td className="py-1.5 px-2">
                          <code className="bg-blue-50 px-1 rounded">Maks kap. - Antall</code>{t('manager.modules.itemLocationBalance.info.calc.availableSpacePart1', {}, '. Tom hvis varen ikke har ')}<code className="bg-blue-50 px-1 rounded">MaxQuantity</code>{t('manager.modules.itemLocationBalance.info.calc.availableSpacePart2', {}, ' konfigurert')}
                        </td>
                      </tr>
                      <tr className="border-b border-blue-100">
                        <td className="py-1.5 px-2 font-medium">{t('warehouse.general.reserved', {}, 'Reservert')}</td>
                        <td className="py-1.5 px-2">
                          <code className="bg-blue-50 px-1 rounded">SUM(StcReservation.ResQuantity)</code>{t('manager.modules.itemLocationBalance.info.calc.reservedPart1', {}, ' hvor ')}<code className="bg-blue-50 px-1 rounded">ResStatus = 0</code>{t('manager.modules.itemLocationBalance.info.calc.reservedPart2', {}, ' (åpen reservasjon), gruppert per Item + Bin')}
                        </td>
                      </tr>
                      <tr>
                        <td className="py-1.5 px-2 font-medium">{t('warehouse.general.available', {}, 'Tilgjengelig')}</td>
                        <td className="py-1.5 px-2">
                          <code className="bg-blue-50 px-1 rounded">SUM(Antall per Item+Bin) - Reservert</code> {t('manager.modules.itemLocationBalance.info.calc.available', {}, '— hva som faktisk kan plukkes/flyttes')}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <h3 className="font-bold text-gray-900 mb-1">{t('manager.modules.itemLocationBalance.info.caveats.heading', {}, 'Pass på')}</h3>
                <ul className="list-disc pl-5 space-y-0.5 text-gray-800">
                  <li>
                    <strong>{t('warehouse.general.reserved', {}, 'Reservert')}</strong>{t('manager.modules.itemLocationBalance.info.caveats.reservedPart1', {}, ' vises kun på én rad per Item+Bin (eldste ID), selv om varen har flere serial-/batch-rader på samme lokasjon. Dette er for å unngå dobbeltelling — ')}<code className="bg-yellow-100 px-1 rounded text-xs">StcReservation</code>{t('manager.modules.itemLocationBalance.info.caveats.reservedPart2', {}, ' er på Item+Bin-nivå, ikke per serial/batch.')}
                  </li>
                  <li>
                    <strong>{t('warehouse.general.available', {}, 'Tilgjengelig')}</strong>{t('manager.modules.itemLocationBalance.info.caveats.available', {}, ' fordeles nedover serial-/batch-rader som deler samme lokasjon, med tak på radens eget antall (eldste rad først). To serienummer-rader à 1 stk uten reservasjon vises som 1 og 1; er én reservert vises 1 og 0. Summen per lokasjon er alltid lik reell tilgjengelighet.')}
                  </li>
                  <li>
                    <strong>{t('manager.modules.itemLocationBalance.columns.maxQuantity', {}, 'Maks kap.')}</strong>{t('manager.modules.itemLocationBalance.info.caveats.capacityPart1', {}, ' og dermed ')}<strong>{t('manager.modules.itemLocationBalance.columns.availableSpace', {}, 'Ledig kap.')}</strong>{t('manager.modules.itemLocationBalance.info.caveats.capacityPart2', {}, ' kan være tomme hvis verken direkte ItemBinSize-rad eller gruppe-konfig finnes for kombinasjonen Item × BinSize.')}
                  </li>
                  <li>
                    {t('manager.modules.itemLocationBalance.info.caveats.openPart1', {}, 'Kun ')}<strong>{t('manager.modules.itemLocationBalance.info.caveats.openStrong', {}, 'åpne')}</strong>{t('manager.modules.itemLocationBalance.info.caveats.openPart2', {}, ' reservasjoner (')}<code className="bg-yellow-100 px-1 rounded text-xs">ResStatus = 0</code>{t('manager.modules.itemLocationBalance.info.caveats.openPart3', {}, ') telles. Reservasjoner som er konsumert eller kansellert ekskluderes.')}
                  </li>
                  <li>
                    {t('manager.modules.itemLocationBalance.info.caveats.flowPart1', {}, 'Tallene inkluderer ikke vareflyt som er igangsatt men ikke registrert i ')}<code className="bg-yellow-100 px-1 rounded text-xs">StcReservation</code>{t('manager.modules.itemLocationBalance.info.caveats.flowPart2', {}, ' (f.eks. pågående intern flytting via ')}<code className="bg-yellow-100 px-1 rounded text-xs">StcIntRelocation</code>{t('manager.modules.itemLocationBalance.info.caveats.flowPart3', {}, ').')}
                  </li>
                </ul>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Reusable Warehouse Table - handles everything else */}
      <div className="flex-1 min-h-0">
        <WarehouseTable
          data={filteredData}
          columns={columns}
          config={{
            showPagination: true,
            showColumnConfig: true,
            showGlobalSearch: false, // We handle this ourselves
            showFilters: true,
            showExport: true,
            showPrint: true,
            showSummary: true,
            defaultPageSize: 100,
            allowColumnReorder: true,
            allowColumnResize: true,
            stickyHeader: true,
            compactMode: true,
            persistenceKey: 'item-location-balance'
          }}
          summaryCalculations={summaryCalculations}
          rowActions={isManagerOrAdmin ? correctionRowActions : undefined}
          loading={loading}
          error={error || undefined}
        />
      </div>

      {/* Rett serienummer-modal (kun lagersjef/administrator) */}
      {correctSerialRow && (
        <CorrectSerialModal
          row={correctSerialRow}
          onClose={() => setCorrectSerialRow(null)}
          onSuccess={(message) => {
            setCorrectSerialRow(null);
            alert(message);
            loadItemBins();
          }}
        />
      )}

      {/* Rett batch/utløpsdato-modal (kun lagersjef/administrator, varetype 1/4/5) */}
      {correctBatchRow && (
        <CorrectBatchModal
          row={correctBatchRow}
          onClose={() => setCorrectBatchRow(null)}
          onSuccess={(message) => {
            setCorrectBatchRow(null);
            alert(message);
            loadItemBins();
          }}
        />
      )}
    </div>
  );
};

export default ItemLocationBalanceApp;